import { supabase } from './supabase';
import { Task, WorkHoursConfig, CalendarEvent } from '../types/index';
import {
  addMinutes,
  addDays,
  parseISO,
  differenceInMinutes,
  setHours,
  setMinutes,
  startOfDay,
} from 'date-fns';

export interface TimeSlot {
  start: Date;
  end: Date;
}

export interface ScheduleResult {
  task: Task;
  blocks: TimeSlot[];
  unscheduledMinutes: number;
}

const DAY_NAME_TO_NUMBER: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

function roundUpTo15(date: Date): Date {
  const minutes = date.getMinutes();
  const remainder = minutes % 15;
  if (remainder === 0) return new Date(date.getTime());
  return addMinutes(date, 15 - remainder);
}

function subtractInterval(slots: TimeSlot[], blocked: TimeSlot): TimeSlot[] {
  const result: TimeSlot[] = [];
  for (const slot of slots) {
    if (blocked.end.getTime() <= slot.start.getTime() || blocked.start.getTime() >= slot.end.getTime()) {
      result.push(slot);
      continue;
    }
    if (blocked.start.getTime() > slot.start.getTime()) {
      result.push({ start: new Date(slot.start.getTime()), end: new Date(blocked.start.getTime()) });
    }
    if (blocked.end.getTime() < slot.end.getTime()) {
      result.push({ start: new Date(blocked.end.getTime()), end: new Date(slot.end.getTime()) });
    }
  }
  return result;
}

function parseTime(timeStr: string, date: Date): Date {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return setMinutes(setHours(new Date(date.getTime()), hours), minutes);
}

function isWorkDay(date: Date, workDays: string[]): boolean {
  const dow = date.getDay();
  return workDays.some(name => DAY_NAME_TO_NUMBER[name.toLowerCase()] === dow);
}

function buildWorkSlotsForDay(date: Date, config: WorkHoursConfig, minStartTime: Date): TimeSlot[] {
  if (!isWorkDay(date, config.work_days)) return [];

  const morningStart = parseTime(config.morning_start, date);
  const morningEnd = parseTime(config.morning_end, date);
  const afternoonStart = parseTime(config.afternoon_start, date);
  const afternoonEnd = parseTime(config.afternoon_end, date);

  const slots: TimeSlot[] = [];

  if (morningEnd.getTime() > minStartTime.getTime()) {
    const effectiveStart = morningStart.getTime() > minStartTime.getTime() ? morningStart : new Date(minStartTime.getTime());
    if (effectiveStart.getTime() < morningEnd.getTime()) {
      slots.push({ start: effectiveStart, end: morningEnd });
    }
  }

  if (afternoonEnd.getTime() > minStartTime.getTime()) {
    const effectiveStart = afternoonStart.getTime() > minStartTime.getTime() ? afternoonStart : new Date(minStartTime.getTime());
    if (effectiveStart.getTime() < afternoonEnd.getTime()) {
      slots.push({ start: effectiveStart, end: afternoonEnd });
    }
  }

  return slots;
}

function buildFreeSlots(now: Date, config: WorkHoursConfig, blockedIntervals: TimeSlot[]): TimeSlot[] {
  const horizon = addDays(now, 90);
  let freeSlots: TimeSlot[] = [];

  let currentDate = startOfDay(now);
  while (currentDate.getTime() <= horizon.getTime()) {
    const daySlots = buildWorkSlotsForDay(currentDate, config, now);
    freeSlots.push(...daySlots);
    currentDate = addDays(currentDate, 1);
  }

  for (const blocked of blockedIntervals) {
    freeSlots = subtractInterval(freeSlots, blocked);
  }

  return freeSlots;
}

export class TaskScheduler {
  private config: WorkHoursConfig;
  private externalEvents: CalendarEvent[];
  private manualTasks: TimeSlot[];
  private now: Date;

  constructor(config: WorkHoursConfig, externalEvents: CalendarEvent[] = [], manualTasks: TimeSlot[] = []) {
    this.config = config;
    this.externalEvents = externalEvents;
    this.manualTasks = manualTasks;
    this.now = new Date();
  }

  private computeEffectiveStart(task: Task): Date {
    const windowStart = task.fecha_inicio
      ? Math.max(parseISO(task.fecha_inicio).getTime(), this.now.getTime())
      : this.now.getTime();

    if (!task.fecha_fin) return new Date(windowStart);

    const deadline = parseISO(task.fecha_fin).getTime() + 24 * 60 * 60 * 1000;
    if (deadline <= windowStart) return new Date(windowStart);

    const rawRemaining = Math.round(
      (task.horas_estimadas - (task.horas_ejecutadas || 0)) * 60
    );
    const remainingMinutes = rawRemaining > 0 ? rawRemaining : 30;
    const workMinutesPerDay = this.getWorkMinutesPerDay();
    const estimatedDays = Math.ceil(remainingMinutes / (workMinutesPerDay * 0.8));

    const msPerDay = 24 * 60 * 60 * 1000;
    const availableDays = Math.floor((deadline - windowStart) / msPerDay);

    if (availableDays <= estimatedDays * 2) {
      return new Date(windowStart);
    }

    const bufferDays = Math.max(1, estimatedDays);
    const latestStart = deadline - bufferDays * msPerDay;
    const midpoint = windowStart + (deadline - windowStart) / 2;
    const deferredStart = Math.max(midpoint, latestStart);
    const effectiveStart = Math.max(windowStart, Math.min(deferredStart, deadline - msPerDay));

    return new Date(effectiveStart);
  }

  private getWorkMinutesPerDay(): number {
    const morningMinutes = this.timeToMinutes(this.config.morning_end) - this.timeToMinutes(this.config.morning_start);
    const afternoonMinutes = this.timeToMinutes(this.config.afternoon_end) - this.timeToMinutes(this.config.afternoon_start);
    return morningMinutes + afternoonMinutes;
  }

  private timeToMinutes(timeStr: string): number {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  }

  public scheduleTasks(tasks: Task[]): ScheduleResult[] {
    const priorityValue = (p: string): number => {
      if (p === 'alta') return 3;
      if (p === 'normal') return 2;
      return 1;
    };

    const eligible = tasks.filter(t =>
      (t.estado === 'por_empezar' || t.estado === 'en_marcha') &&
      !t.exclude_from_auto_schedule
    );

    const sorted = [...eligible].sort((a, b) => {
      const aIsRodajeFecha = a.tipo === 'rodaje' && !!a.fecha_fin;
      const bIsRodajeFecha = b.tipo === 'rodaje' && !!b.fecha_fin;
      if (aIsRodajeFecha && !bIsRodajeFecha) return -1;
      if (!aIsRodajeFecha && bIsRodajeFecha) return 1;

      const prioDiff = priorityValue(b.prioridad || 'normal') - priorityValue(a.prioridad || 'normal');
      if (prioDiff !== 0) return prioDiff;

      const aEffStart = this.computeEffectiveStart(a);
      const bEffStart = this.computeEffectiveStart(b);
      const startDiff = aEffStart.getTime() - bEffStart.getTime();
      if (startDiff !== 0) return startDiff;

      const aHasEnd = !!a.fecha_fin;
      const bHasEnd = !!b.fecha_fin;
      if (aHasEnd && bHasEnd) {
        return parseISO(a.fecha_fin!).getTime() - parseISO(b.fecha_fin!).getTime();
      }
      if (aHasEnd) return -1;
      if (bHasEnd) return 1;

      return (b.scheduling_priority || 0) - (a.scheduling_priority || 0);
    });

    const blockedIntervals: TimeSlot[] = [
      ...this.externalEvents.map(e => ({
        start: parseISO(e.start_time),
        end: parseISO(e.end_time),
      })),
      ...this.manualTasks,
    ];

    let globalFreeSlots = buildFreeSlots(this.now, this.config, blockedIntervals);

    const results: ScheduleResult[] = [];

    for (const task of sorted) {
      if (task.is_manually_positioned) {
        results.push({ task, blocks: [], unscheduledMinutes: 0 });
        continue;
      }

      const rawRemaining = Math.round(
        (task.horas_estimadas - (task.horas_ejecutadas || 0)) * 60
      );
      const remainingMinutes = rawRemaining > 0 ? rawRemaining : 30;

      let searchSlots = globalFreeSlots;

      if (task.fecha_fin) {
        const taskDeadline = parseISO(task.fecha_fin);
        const inclusiveDeadline = new Date(taskDeadline.getTime() + 24 * 60 * 60 * 1000);
        if (inclusiveDeadline.getTime() > this.now.getTime()) {
          searchSlots = globalFreeSlots.filter(
            s => s.start.getTime() < inclusiveDeadline.getTime()
          );
        }
      }

      const effectiveStart = this.computeEffectiveStart(task);
      if (effectiveStart.getTime() > this.now.getTime()) {
        searchSlots = searchSlots
          .filter(s => s.end.getTime() > effectiveStart.getTime())
          .map(s => s.start.getTime() < effectiveStart.getTime()
            ? { start: new Date(effectiveStart.getTime()), end: s.end }
            : s
          );
      } else if (task.fecha_inicio) {
        const taskStart = parseISO(task.fecha_inicio);
        const effStart = taskStart.getTime() > this.now.getTime() ? taskStart : this.now;
        searchSlots = searchSlots
          .filter(s => s.end.getTime() > effStart.getTime())
          .map(s => s.start.getTime() < effStart.getTime()
            ? { start: new Date(effStart.getTime()), end: s.end }
            : s
          );
      }

      if (task.tipo === 'rodaje' && task.fecha_fin) {
        const { blocks, unscheduledMinutes, consumedSlots } = this.assignRodajeBlock(
          remainingMinutes,
          searchSlots
        );
        for (const consumed of consumedSlots) {
          globalFreeSlots = subtractInterval(globalFreeSlots, consumed);
        }
        results.push({ task, blocks, unscheduledMinutes });
        continue;
      }

      const { blocks, unscheduledMinutes, consumedSlots } = this.assignBlocksToTask(
        remainingMinutes,
        searchSlots
      );

      for (const consumed of consumedSlots) {
        globalFreeSlots = subtractInterval(globalFreeSlots, consumed);
      }

      results.push({ task, blocks, unscheduledMinutes });
    }

    return results;
  }

  private assignBlocksToTask(
    remainingMinutes: number,
    freeSlots: TimeSlot[]
  ): { blocks: TimeSlot[]; unscheduledMinutes: number; consumedSlots: TimeSlot[] } {
    const { max_task_block_minutes, min_task_block_minutes, buffer_between_tasks_minutes } = this.config;
    const blocks: TimeSlot[] = [];
    const consumedSlots: TimeSlot[] = [];
    let localFreeSlots = freeSlots.map(s => ({ start: new Date(s.start.getTime()), end: new Date(s.end.getTime()) }));
    let remaining = remainingMinutes;

    while (remaining > 0) {
      const desired = Math.min(remaining, max_task_block_minutes);

      let foundSlotIdx = -1;
      for (let i = 0; i < localFreeSlots.length; i++) {
        const slot = localFreeSlots[i];
        const blockStart = roundUpTo15(slot.start);
        const usable = differenceInMinutes(slot.end, blockStart);
        if (usable >= desired) {
          foundSlotIdx = i;
          break;
        }
      }

      if (foundSlotIdx === -1 && desired > min_task_block_minutes) {
        let bestIdx = -1;
        let bestUsable = 0;
        for (let i = 0; i < localFreeSlots.length; i++) {
          const slot = localFreeSlots[i];
          const blockStart = roundUpTo15(slot.start);
          const usable = differenceInMinutes(slot.end, blockStart);
          if (usable >= min_task_block_minutes && usable > bestUsable) {
            bestUsable = usable;
            bestIdx = i;
          }
        }
        foundSlotIdx = bestIdx;
      }

      if (foundSlotIdx === -1) {
        console.warn(`No slot >= min_task_block_minutes for task, remaining: ${remaining}min`);
        break;
      }

      const slot = localFreeSlots[foundSlotIdx];
      const blockStart = roundUpTo15(slot.start);
      const blockDuration = Math.min(desired, differenceInMinutes(slot.end, blockStart));
      const blockEnd = addMinutes(blockStart, blockDuration);

      if (blockEnd.getTime() > slot.end.getTime()) {
        console.warn('Block would exceed slot boundary, skipping');
        break;
      }

      const actualDuration = differenceInMinutes(blockEnd, blockStart);
      blocks.push({ start: blockStart, end: blockEnd });

      const bufferEnd = addMinutes(blockEnd, buffer_between_tasks_minutes);
      const toConsume: TimeSlot = { start: blockStart, end: bufferEnd };
      consumedSlots.push(toConsume);
      localFreeSlots = subtractInterval(localFreeSlots, toConsume);

      remaining -= actualDuration;
    }

    return { blocks, unscheduledMinutes: Math.max(0, remaining), consumedSlots };
  }

  private assignRodajeBlock(
    remainingMinutes: number,
    freeSlots: TimeSlot[]
  ): { blocks: TimeSlot[]; unscheduledMinutes: number; consumedSlots: TimeSlot[] } {
    const { buffer_between_tasks_minutes } = this.config;

    for (const slot of freeSlots) {
      const blockStart = roundUpTo15(slot.start);
      const usable = differenceInMinutes(slot.end, blockStart);
      if (usable >= remainingMinutes) {
        const blockEnd = addMinutes(blockStart, remainingMinutes);
        const bufferEnd = addMinutes(blockEnd, buffer_between_tasks_minutes);
        const consumed: TimeSlot = { start: blockStart, end: bufferEnd };
        return {
          blocks: [{ start: blockStart, end: blockEnd }],
          unscheduledMinutes: 0,
          consumedSlots: [consumed],
        };
      }
    }

    return this.assignBlocksToTask(remainingMinutes, freeSlots);
  }

  public async saveSchedule(results: ScheduleResult[], userId: string): Promise<void> {
    const nonManualTaskIds = results
      .filter(r => !r.task.is_manually_positioned)
      .map(r => r.task.id);

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    if (nonManualTaskIds.length > 0) {
      await supabase
        .from('apro_task_blocks')
        .delete()
        .eq('user_id', userId)
        .in('task_id', nonManualTaskIds)
        .gte('start_time', todayStart.toISOString());
    }

    const allBlockRows: {
      task_id: string;
      user_id: string;
      block_index: number;
      start_time: string;
      end_time: string;
      duration_minutes: number;
    }[] = [];

    for (const result of results) {
      if (result.task.is_manually_positioned) continue;
      if (result.blocks.length === 0) continue;

      result.blocks.forEach((block, index) => {
        allBlockRows.push({
          task_id: result.task.id,
          user_id: userId,
          block_index: index,
          start_time: block.start.toISOString(),
          end_time: block.end.toISOString(),
          duration_minutes: differenceInMinutes(block.end, block.start),
        });
      });
    }

    if (allBlockRows.length > 0) {
      await supabase.from('apro_task_blocks').insert(allBlockRows);
    }

    for (const result of results) {
      if (result.task.is_manually_positioned) continue;

      if (result.blocks.length === 0) {
        await supabase
          .from('apro_tasks')
          .update({
            scheduled_start: null,
            scheduled_end: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', result.task.id);
      } else {
        const firstBlock = result.blocks[0];
        const lastBlock = result.blocks[result.blocks.length - 1];
        const icalUid = result.task.ical_uid || `${result.task.id}@apro-calendar`;

        await supabase
          .from('apro_tasks')
          .update({
            scheduled_start: firstBlock.start.toISOString(),
            scheduled_end: lastBlock.end.toISOString(),
            ical_uid: icalUid,
            updated_at: new Date().toISOString(),
          })
          .eq('id', result.task.id);
      }
    }
  }

  public static async getOrCreateConfig(userId: string): Promise<WorkHoursConfig> {
    const { data: existing } = await supabase
      .from('apro_work_hours_config')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (existing) return existing;

    const defaultConfig = {
      user_id: userId,
      morning_start: '10:00',
      morning_end: '14:00',
      afternoon_start: '15:30',
      afternoon_end: '18:30',
      work_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      min_task_block_minutes: 30,
      max_task_block_minutes: 120,
      buffer_between_tasks_minutes: 15,
    };

    const { data, error } = await supabase
      .from('apro_work_hours_config')
      .insert(defaultConfig)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}
